import { LogoutButton } from "./logout";
import { BaseContext } from "./base-context";
import { Button } from "../catalyst-components/button";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { MainRoutes } from "./models";
import { Divider } from "../catalyst-components/divider";
import { Switch, SwitchField } from "../catalyst-components/switch";
import { Label } from "../catalyst-components/fieldset";
import { useLocalStorageImage } from "./hooks/useLocalStorageImage";
import { useStableQuery } from "./hooks/useStableQuery";
import { api } from "../convex/_generated/api";
import { Id } from "../convex/_generated/dataModel";
import { InnerWeatherWidget } from "../components/InnerWeatherWidget";
import { useRouteCondition } from "./hooks/useRouteCondition";
import { FeedbackDialog } from "./components/FeedbackDialog";
import { ChatBubbleBottomCenterTextIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { LanguageSwitcher } from "./components/LanguageSwitcher";


export const ProfileMenu: React.FC = () => {
    const { t } = useTranslation();
    const { user, convexUserId } = useContext(BaseContext);
    const userMetadata = useStableQuery(api.user_metadata.getUserMetadata, convexUserId ? { user_id: convexUserId as Id<"users"> } : 'skip');
    const userProgress = useStableQuery(api.user_progress.getByUserId, convexUserId ? { user_id: convexUserId as Id<"users"> } : 'skip');
    const scoreHistory = useStableQuery(api.user_progress.getScoreHistory, convexUserId ? { user_id: convexUserId as Id<"users"> } : 'skip');
    const onboardingCompleted = useStableQuery(api.user_metadata.getOnboardingCompleted, convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip");
    const completedSessions = useStableQuery(api.completed_sessions.listByUser, convexUserId ? { user_id: convexUserId as Id<"users">, limit: 1 } : "skip");
    const hasCompletedSessions = completedSessions && completedSessions.length > 0;
    const navigate = useNavigate();
    const { setIsSidebarShown, setDebug, debug, isAdmin } = useContext(BaseContext);

    const isPersonalAssistantEnabled = useRouteCondition('IsPersonalAssistantEnabled');

    const [userName, setUserName] = useState('');
    const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);

    const profileImage = useLocalStorageImage({
        url: user?.imageUrl || '',
        alt: `${userName}'s profile picture`,
        className: "h-14 rounded-full border-primary-3 border-4 justify-center"
    });

    const handleNavigation = (route: string) => {
        navigate(route);
        setIsSidebarShown(false);
    }

    useEffect(() => {
        if (userMetadata !== undefined) {
            setUserName(userMetadata?.nickname || userMetadata?.legal_name || user?.fullName || '');
        }
    }, [userMetadata, user]);

    return (
        <BaseContext.Consumer>
            {({ setIsSidebarShown}) => (
                <section className="flex flex-col w-full h-full lg:w-80 fixed top-0 right-0 z-50  bg-primary-1">
                    {/* Close button — mobile only */}
                    <button
                        onClick={() => setIsSidebarShown(false)}
                        className="lg:hidden absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-primary-2 transition-colors"
                        aria-label="Close menu"
                    >
                        <XMarkIcon className="h-6 w-6 text-foreground" />
                    </button>

                    <section className="my-4 flex flex-col">
                        <button onClick={() => setIsSidebarShown(false)} className="flex flex-row justify-center pt-6">
                            {profileImage}
                        </button>
                        <span className="w-full text-center mt-2">{userName}</span>
                        
                        {/* Inner Weather — fixed height to prevent layout shift */}
                        <div className="mt-3 px-4">
                            <div className="bg-primary-1 rounded-lg p-2 border border-primary-4 h-[180px] overflow-hidden flex flex-col">
                                <h4 className="text-xs font-medium text-primary-5 text-center mb-1">{t('profileMenu.innerWeather')}</h4>
                                {userProgress === undefined ? (
                                    <div className="flex-1 flex items-center justify-center">
                                        <img src="/boar_track_v2_a_png.png" alt="Loading..." className="h-8 w-8" />
                                    </div>
                                ) : userProgress ? (
                                    <InnerWeatherWidget
                                        a={userProgress.n_score}
                                        b={userProgress.wb_score}
                                        c={userProgress.se_score}
                                        variant="full"
                                        trend={scoreHistory ?? undefined}
                                        hideDiscuss={!hasCompletedSessions}
                                    />
                                ) : (
                                    <p className="text-xs text-muted-foreground text-center py-4">
                                        Complete more sessions to see your Inner Weather
                                    </p>
                                )}
                            </div>
                        </div>
                    </section>
                    {onboardingCompleted && (
                        <>
                            <Divider/>
                            <section className="flex flex-col justify-center items-center my-6">
                                <Button color="light" className="w-52" onClick={() => handleNavigation(MainRoutes.Progress)}>{t('common.home')}</Button>
                                <Button color="light" className="w-52 mt-2" onClick={() => handleNavigation(MainRoutes.Skills)}>{t('profileMenu.mySkills')}</Button>
                                {isPersonalAssistantEnabled && (
                                    <Button color="light" className="w-52 mt-2" onClick={() => handleNavigation(MainRoutes.MyChat)}>{t('profileMenu.chat')}</Button>
                                )}
                                <Button color="light" className="w-52 mt-2" onClick={() => handleNavigation(MainRoutes.Profile)}>{t('profileMenu.me')}</Button>
                                <Button
                                    color="light"
                                    className="w-52 mt-2"
                                    onClick={() => setShowFeedbackDialog(true)}
                                >
                                    <ChatBubbleBottomCenterTextIcon className="h-4 w-4 mr-2" />
                                    {t('profileMenu.giveFeedback')}
                                </Button>
                            </section>
                        </>
                    )}
                    {isAdmin && (
                        <>                    
                            <Divider/>
                            <section className="flex flex-col justify-center items-center my-6">
                                <Button color="light" className="w-52" onClick={() => handleNavigation(MainRoutes.Admin)}>{t('profileMenu.admin')}</Button>
                            </section>
                            <Divider/>
                            <section className="px-4 mt-2">
                                <span>{t('profileMenu.debugTools')}</span>
                                <SwitchField className="mt-2">
                                    <Label>{t('profileMenu.enableDebugging')}</Label>
                                    <Switch name="allow_debug_messages" checked={debug} onClick={(e) => setDebug(!debug)} />
                                </SwitchField>
                            </section>
                        </>
                    )}
                    <section className="mt-auto">
                        <div className="flex justify-center px-4 py-3 border-t border-border/30">
                            <LanguageSwitcher variant="compact" />
                        </div>
                        <LogoutButton />
                    </section>

                    <FeedbackDialog
                        open={showFeedbackDialog}
                        onClose={() => setShowFeedbackDialog(false)}
                    />
                </section>
            )}
        </BaseContext.Consumer>
    );
}